// PHI Minimization and Privacy Controls

/**
 * Mask a patient name to show only initials
 * e.g., "John Smith" -> "J.S."
 */
export function maskPatientName(firstName?: string, lastName?: string): string {
  if (!firstName && !lastName) return '—';
  
  const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
  const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
  
  if (firstInitial && lastInitial) {
    return `${firstInitial}.${lastInitial}.`;
  }
  return firstInitial || lastInitial || '—';
}

/**
 * Mask a full name to show only initials
 * e.g., "John Michael Smith" -> "J.M.S."
 */
export function maskFullName(fullName?: string): string {
  if (!fullName) return '—';
  
  const parts = fullName.trim().split(/\s+/);
  return parts.map(p => p.charAt(0).toUpperCase()).join('.') + '.';
}

/**
 * Mask an MRN (Medical Record Number)
 * e.g., "MRN123456" -> "MRN***456"
 */
export function maskMRN(mrn?: string): string {
  if (!mrn) return '—';
  if (mrn.length <= 3) return '***';
  
  return mrn.slice(0, 3) + '***' + mrn.slice(-3);
}

/**
 * Mask a member ID
 * e.g., "ABC123456789" -> "ABC***789"
 */
export function maskMemberId(memberId?: string): string {
  if (!memberId) return '—';
  if (memberId.length <= 6) return '***' + memberId.slice(-3);
  
  return memberId.slice(0, 3) + '***' + memberId.slice(-3);
}

/**
 * Mask date of birth to show only year
 * e.g., "1985-03-15" -> "****-**-** (1985)"
 */
export function maskDOB(dob?: string): string {
  if (!dob) return '—';
  
  const year = dob.split('-')[0];
  return `****-**-** (${year})`;
}

/**
 * Mask an email address
 * e.g., "john.smith@hospital.com" -> "j***@h***.com"
 */
export function maskEmail(email?: string): string {
  if (!email) return '—';
  
  const [local, domain] = email.split('@');
  if (!domain) return '***@***';
  
  const [domainName, ...tlds] = domain.split('.');
  const tld = tlds.join('.');
  
  const maskedLocal = local.charAt(0) + '***';
  const maskedDomain = domainName.charAt(0) + '***';
  
  return `${maskedLocal}@${maskedDomain}.${tld}`;
}

/**
 * Mask a phone number
 * e.g., "555-123-4567" -> "***-***-4567"
 */
export function maskPhone(phone?: string): string {
  if (!phone) return '—';
  
  // Remove non-digits
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return '***';
  
  return '***-***-' + digits.slice(-4);
}

/**
 * Mask a Social Security Number
 * e.g., "123-45-6789" -> "***-**-6789"
 */
export function maskSSN(ssn?: string): string {
  if (!ssn) return '—';
  
  const digits = ssn.replace(/\D/g, '');
  if (digits.length < 4) return '***-**-****';
  
  return '***-**-' + digits.slice(-4);
}

/**
 * Mask an address to show only city/state
 * e.g., "123 Main St, Anytown, OH 45678" -> "***, Anytown, OH *****"
 */
export function maskAddress(address?: string): string {
  if (!address) return '—';
  
  // Try to extract city and state from common address formats
  const parts = address.split(',').map(p => p.trim());
  if (parts.length >= 2) {
    // Assume format: "Street, City, State ZIP" or "Street, City State ZIP"
    const cityStateZip = parts.slice(-1)[0] || '';
    const city = parts.slice(-2, -1)[0] || '';
    
    // Mask the zip
    const masked = cityStateZip.replace(/\d{5}(-\d{4})?/, '*****');
    
    return `***, ${city}, ${masked}`;
  }
  
  return '*** [Address Masked]';
}

/**
 * Mask sensitive identifiers in log messages
 */
export function maskLogMessage(message: string): string {
  let masked = message;
  
  // Mask SSN patterns
  masked = masked.replace(/\b\d{3}-\d{2}-\d{4}\b/g, '***-**-****');
  
  // Mask phone patterns
  masked = masked.replace(/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, '***-***-****');
  
  // Mask email patterns
  masked = masked.replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '***@***.***');
  
  // Mask dates in YYYY-MM-DD format (potential DOBs)
  masked = masked.replace(/\b(19|20)\d{2}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])\b/g, '****-**-**');
  
  return masked;
}

/**
 * Create a patient display object with masked PHI
 */
export interface PatientDisplay {
  initials: string;
  maskedMRN: string;
  maskedDOB: string;
  maskedMemberId: string;
  internalId: string;
}

export function createMaskedPatientDisplay(patient: {
  id: string;
  first_name?: string;
  last_name?: string;
  mrn?: string;
  dob?: string;
  member_id?: string;
}): PatientDisplay {
  return {
    initials: maskPatientName(patient.first_name, patient.last_name),
    maskedMRN: maskMRN(patient.mrn),
    maskedDOB: maskDOB(patient.dob),
    maskedMemberId: maskMemberId(patient.member_id),
    internalId: patient.id.slice(0, 8), // First 8 chars of UUID
  };
}

/**
 * Check if the current context allows full PHI display
 * This should be gated by the 'sensitive.full_phi' permission
 */
export function shouldMaskPHI(userPermissions: string[], phiMaskingEnabled: boolean = true): boolean {
  if (!phiMaskingEnabled) return false;
  return !userPermissions.includes('sensitive.full_phi');
}
